import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import WappIcono from "../../../components/wappIcono";
import "./marcarFoto.css";

/**
 * Fotos antes de mandarlas (Esteban, 2026-10-26: "envié imagen con mensaje en WappTeam y se tardó un mundo en irse" y
 * "que puedan marcarla como en WhatsApp para cuando ocupen circular algo").
 *
 * 1. comprimirFoto: como WhatsApp, la foto se achica a 2048 px por lado y sale en JPEG desde el navegador. Una foto de
 *    celular de 4–8 MB queda en ~300–600 KB: lo que tardaba era subir la foto cruda (hasta 20 s en la oficina), no el
 *    servidor. Lo que el navegador no sabe abrir (HEIC en Chrome) se manda tal cual y lo convierte el servidor.
 * 2. MarcarFoto: lápiz, flecha, círculo, cuadro y texto encima de la foto, con colores, grosor y deshacer. Sale un JPEG
 *    nuevo con las marcas "quemadas", listo para mandar.
 * 3. Varias fotos (Esteban, 2026-10-27: "no fue fácil pasar a la siguiente foto en el celular, debería ser igual a WhatsApp"):
 *    todas van dentro del editor con su tira de miniaturas; sin herramienta elegida se desliza el dedo para cambiar de
 *    foto, cada una guarda sus marcas, y se puede escribir el comentario y enviar desde ahí.
 */

/** Lado máximo de la foto que se manda (como WhatsApp). */
export const LADO_MAX = 2048;
/** Por debajo de esto no vale la pena recomprimir. */
const CHICA = 300 * 1024;

const nombreJpg = (n: string) => (n || "foto").replace(/\.[a-z0-9]+$/i, "") + ".jpg";
const aBlob = (c: HTMLCanvasElement, calidad: number): Promise<Blob | null> => new Promise((r) => c.toBlob((b) => r(b), "image/jpeg", calidad));

/** Carga la imagen en el navegador; null si no la sabe abrir (HEIC en Chrome/Windows, archivo dañado). */
const cargarImagen = (file: Blob): Promise<HTMLImageElement | null> => new Promise((resolve) => {
	const url = URL.createObjectURL(file);
	const img = new Image();
	img.onload = () => { URL.revokeObjectURL(url); resolve(img.naturalWidth > 0 ? img : null); };
	img.onerror = () => { URL.revokeObjectURL(url); resolve(null); };
	img.src = url;
});

/** GIF y WEBP se mandan tal cual (pueden ser animados; el servidor también los respeta). */
const esAnimable = (f: File) => /^image\/(gif|webp)$/i.test(f.type) || /\.(gif|webp)$/i.test(f.name || "");

/** La foto dibujada a tamaño de envío (≤ 2048 px por lado, fondo blanco bajo lo transparente, derecha según el EXIF). */
const lienzoBase = (img: HTMLImageElement): HTMLCanvasElement | null => {
	const esc = Math.min(1, LADO_MAX / Math.max(img.naturalWidth, img.naturalHeight));
	const c = document.createElement("canvas");
	c.width = Math.max(1, Math.round(img.naturalWidth * esc));
	c.height = Math.max(1, Math.round(img.naturalHeight * esc));
	const ctx = c.getContext("2d");
	if (!ctx) return null;
	ctx.imageSmoothingEnabled = true;
	ctx.imageSmoothingQuality = "high";
	ctx.fillStyle = "#fff";
	ctx.fillRect(0, 0, c.width, c.height);
	ctx.drawImage(img, 0, 0, c.width, c.height);
	return c;
};

/**
 * Achica y recomprime una foto antes de subirla. Devuelve el mismo archivo cuando ya es chica, es GIF/WEBP, el navegador
 * no la sabe abrir o la versión nueva no quedó más liviana.
 */
export const comprimirFoto = async (file: File): Promise<File> => {
	if (file.size < CHICA || esAnimable(file)) return file;
	const img = await cargarImagen(file);
	if (!img) return file;
	const c = lienzoBase(img);
	if (!c) return file;
	const b = await aBlob(c, 0.85);
	if (!b || b.size >= file.size) return file;
	return new File([b], nombreJpg(file.name), { type: "image/jpeg", lastModified: Date.now() });
};

// ------------------------------------------------------------------ editor

type Herramienta = "lapiz" | "flecha" | "circulo" | "cuadro" | "texto";
interface Punto { x: number; y: number }
interface Trazo { t: Herramienta; color: string; grosor: number; pts: Punto[]; texto?: string }

const HERRAMIENTAS: { id: Herramienta; nombre: string; icono: React.ReactNode }[] = [
	{ id: "lapiz", nombre: "Lápiz", icono: <svg viewBox="0 0 24 24"><path d="M4 20c4-1 5-3 6-6s3-6 6-7c2-1 4 1 3 3-1 3-5 4-8 5s-5 3-7 5" /></svg> },
	{ id: "flecha", nombre: "Flecha", icono: <svg viewBox="0 0 24 24"><path d="M5 19L19 5M10 5h9v9" /></svg> },
	{ id: "circulo", nombre: "Círculo", icono: <svg viewBox="0 0 24 24"><ellipse cx="12" cy="12" rx="8.5" ry="7" /></svg> },
	{ id: "cuadro", nombre: "Cuadro", icono: <svg viewBox="0 0 24 24"><rect x="4" y="5" width="16" height="14" rx="2" /></svg> },
	{ id: "texto", nombre: "Texto", icono: <svg viewBox="0 0 24 24"><path d="M5 6V4h14v2M12 4v16M9 20h6" /></svg> },
];
const COLORES = ["#E53935", "#FDD835", "#43A047", "#1E88E5", "#FFFFFF", "#111111"];
const GROSORES: { id: number; nombre: string }[] = [{ id: 0.6, nombre: "Fino" }, { id: 1, nombre: "Medio" }, { id: 1.8, nombre: "Grueso" }];

/** Pinta un trazo en coordenadas de la foto. */
const pintar = (ctx: CanvasRenderingContext2D, z: Trazo, base: number) => {
	const g = Math.max(2, base * z.grosor);
	ctx.save();
	ctx.strokeStyle = z.color;
	ctx.fillStyle = z.color;
	ctx.lineWidth = g;
	ctx.lineCap = "round";
	ctx.lineJoin = "round";
	const a = z.pts[0];
	const b = z.pts[z.pts.length - 1];
	if (!a) { ctx.restore(); return; }
	if (z.t === "lapiz") {
		ctx.beginPath();
		ctx.moveTo(a.x, a.y);
		if (z.pts.length === 1) ctx.lineTo(a.x + 0.1, a.y);
		for (let i = 1; i < z.pts.length; i++) ctx.lineTo(z.pts[i].x, z.pts[i].y);
		ctx.stroke();
	} else if (z.t === "flecha") {
		const ang = Math.atan2(b.y - a.y, b.x - a.x);
		const cabeza = Math.max(g * 3.5, 14);
		ctx.beginPath();
		ctx.moveTo(a.x, a.y);
		ctx.lineTo(b.x - Math.cos(ang) * cabeza * 0.6, b.y - Math.sin(ang) * cabeza * 0.6);
		ctx.stroke();
		ctx.beginPath();
		ctx.moveTo(b.x, b.y);
		ctx.lineTo(b.x - cabeza * Math.cos(ang - Math.PI / 6), b.y - cabeza * Math.sin(ang - Math.PI / 6));
		ctx.lineTo(b.x - cabeza * Math.cos(ang + Math.PI / 6), b.y - cabeza * Math.sin(ang + Math.PI / 6));
		ctx.closePath();
		ctx.fill();
	} else if (z.t === "circulo") {
		const rx = Math.abs(b.x - a.x) / 2;
		const ry = Math.abs(b.y - a.y) / 2;
		if (rx > 0.5 && ry > 0.5) {
			ctx.beginPath();
			ctx.ellipse((a.x + b.x) / 2, (a.y + b.y) / 2, rx, ry, 0, 0, Math.PI * 2);
			ctx.stroke();
		}
	} else if (z.t === "cuadro") {
		ctx.strokeRect(Math.min(a.x, b.x), Math.min(a.y, b.y), Math.abs(b.x - a.x), Math.abs(b.y - a.y));
	} else if (z.t === "texto" && z.texto) {
		const tam = Math.round(base * 7 * z.grosor);
		ctx.font = "700 " + tam + "px 'Hanken Grotesk', Arial, sans-serif";
		ctx.textBaseline = "top";
		ctx.lineWidth = Math.max(2, tam / 7);
		ctx.strokeStyle = z.color === "#FFFFFF" || z.color === "#FDD835" ? "rgba(0,0,0,.75)" : "rgba(255,255,255,.85)";
		z.texto.split("\n").forEach((linea, i) => {
			ctx.strokeText(linea, a.x, a.y + i * tam * 1.2);
			ctx.fillText(linea, a.x, a.y + i * tam * 1.2);
		});
	}
	ctx.restore();
};

export interface FotoParaMarcar { id: string; file: File; url: string }
export interface FotoMarcada { id: string; file: File }

const SIN_TRAZOS: Trazo[] = [];
/** Grosor base proporcional a la foto (en una de 2048 px, "medio" ≈ 8 px). */
const unidadDe = (b: HTMLCanvasElement | null | undefined) => (b ? Math.max(3, Math.min(b.width, b.height) / 250) : 4);

/**
 * Editor de marcas, una o varias fotos. onListo recibe solo las fotos que se marcaron (JPEG con las marcas); onEnviar
 * hace lo mismo y además pide mandar. Funciona con mouse y con el dedo.
 */
export const MarcarFoto: React.FC<{
	fotos: FotoParaMarcar[]; inicial?: string;
	onListo: (marcadas: FotoMarcada[]) => void; onCerrar: () => void;
	/** Comentario compartido con la bandeja de abajo (se escribe en cualquiera de los dos). */
	comentario?: string; onComentario?: (t: string) => void;
	/** Sin esto no sale el botón de enviar dentro del editor. */
	onEnviar?: (marcadas: FotoMarcada[]) => void; textoEnviar?: string;
}> = ({ fotos, inicial, onListo, onCerrar, comentario, onComentario, onEnviar, textoEnviar }) => {
	const [i, setI] = useState(() => Math.max(0, fotos.findIndex((f) => f.id === inicial)));
	const idx = Math.min(i, Math.max(0, fotos.length - 1));
	const foto = fotos[idx];
	const varias = fotos.length > 1;
	// Fotos ya abiertas (null = el navegador no la sabe abrir). Se guardan para no recargarlas al ir y venir.
	const bases = useRef(new Map<string, HTMLCanvasElement | null>());
	const cargando = useRef(new Set<string>());
	const montado = useRef(true);
	const [, setCargadas] = useState(0);
	// Como WhatsApp: sin herramienta elegida el dedo cambia de foto; al elegir una, dibuja.
	const [herr, setHerr] = useState<Herramienta | null>(null);
	const [color, setColor] = useState(COLORES[0]);
	const [grosor, setGrosor] = useState(1);
	const [marcas, setMarcas] = useState<Record<string, Trazo[]>>({});
	const [enCurso, setEnCurso] = useState<Trazo | null>(null);
	const [textoEn, setTextoEn] = useState<Punto | null>(null);
	const [texto, setTexto] = useState("");
	const [guardando, setGuardando] = useState(false);
	const [dx, setDx] = useState(0);
	const lienzo = useRef<HTMLCanvasElement>(null);
	const marco = useRef<HTMLDivElement>(null);
	const cajaTexto = useRef<HTMLTextAreaElement>(null);
	const tira = useRef<HTMLDivElement>(null);
	const pintando = useRef(false);
	const abiertoEn = useRef(0);
	const desliz = useRef<{ x: number; y: number } | null>(null);

	const id = foto?.id || "";
	const base = bases.current.get(id) || null;
	const noAbre = bases.current.has(id) && !base;
	const trazos = marcas[id] || SIN_TRAZOS;
	const unidad = unidadDe(base);
	const setTrazos = (fn: (t: Trazo[]) => Trazo[]) => setMarcas((m) => ({ ...m, [id]: fn(m[id] || SIN_TRAZOS) }));
	const hayMarcas = Object.values(marcas).some((t) => t.length) || !!(textoEn && texto.trim());

	useEffect(() => () => { montado.current = false; }, []);
	// Abre la foto actual y deja lista la siguiente.
	useEffect(() => {
		const cargar = (f?: FotoParaMarcar): Promise<void> => {
			if (!f || bases.current.has(f.id) || cargando.current.has(f.id)) return Promise.resolve();
			cargando.current.add(f.id);
			return cargarImagen(f.file).then((img) => {
				bases.current.set(f.id, img ? lienzoBase(img) : null);
				cargando.current.delete(f.id);
				if (montado.current) setCargadas((n) => n + 1);
			});
		};
		cargar(foto).then(() => cargar(fotos[idx + 1]));
	}, [id]);
	// La miniatura activa siempre a la vista.
	useEffect(() => {
		(tira.current?.children[idx] as HTMLElement | undefined)?.scrollIntoView?.({ block: "nearest", inline: "center", behavior: "smooth" });
	}, [idx]);

	// Repinta: la foto + los trazos + el que se está dibujando.
	useEffect(() => {
		const c = lienzo.current;
		if (!c || !base) return;
		if (c.width !== base.width || c.height !== base.height) { c.width = base.width; c.height = base.height; }
		const ctx = c.getContext("2d");
		if (!ctx) return;
		ctx.drawImage(base, 0, 0);
		trazos.forEach((z) => pintar(ctx, z, unidad));
		if (enCurso) pintar(ctx, enCurso, unidad);
	}, [base, trazos, enCurso, unidad]);

	useEffect(() => { if (textoEn) setTimeout(() => cajaTexto.current?.focus(), 0); }, [textoEn]);

	const confirmarTexto = () => {
		if (textoEn && texto.trim()) {
			const z: Trazo = { t: "texto", color, grosor, pts: [textoEn], texto: texto.trim() };
			setTrazos((t) => [...t, z]);
		}
		setTextoEn(null); setTexto("");
	};
	const ir = (n: number) => {
		if (n < 0 || n >= fotos.length || n === idx) return;
		confirmarTexto(); pintando.current = false; setEnCurso(null); setI(n);
	};
	const elegirHerr = (h: Herramienta) => { confirmarTexto(); setHerr((a) => (a === h ? null : h)); };
	const cancelar = () => {
		if (guardando) return;
		if (hayMarcas && !window.confirm("¿Descartar las marcas?")) return;
		onCerrar();
	};

	useEffect(() => {
		const tecla = (e: KeyboardEvent) => {
			if (e.key === "Escape") { if (textoEn) setTextoEn(null); else cancelar(); }
			if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "z" && !textoEn) { e.preventDefault(); setTrazos((t) => t.slice(0, -1)); }
			if (!textoEn && (e.key === "ArrowLeft" || e.key === "ArrowRight")) ir(idx + (e.key === "ArrowRight" ? 1 : -1));
		};
		window.addEventListener("keydown", tecla);
		return () => window.removeEventListener("keydown", tecla);
	});

	/** De la pantalla a coordenadas de la foto. */
	const punto = (e: React.PointerEvent): Punto => {
		const c = lienzo.current!;
		const r = c.getBoundingClientRect();
		return { x: Math.max(0, Math.min(c.width, ((e.clientX - r.left) / r.width) * c.width)), y: Math.max(0, Math.min(c.height, ((e.clientY - r.top) / r.height) * c.height)) };
	};
	const bajar = (e: React.PointerEvent) => {
		if (!base || guardando || !herr) return;   // sin herramienta: lo toma el marco para deslizar
		if (e.pointerType === "mouse" && e.button !== 0) return;
		e.stopPropagation();
		const p = punto(e);
		if (herr === "texto") {
			// Sin esto el mousedown que sigue le quita el foco al cuadro recién abierto y lo cierra vacío.
			e.preventDefault();
			confirmarTexto(); setTextoEn(p); setTexto(""); abiertoEn.current = Date.now();
			return;
		}
		if (textoEn) { confirmarTexto(); return; }
		e.preventDefault();
		(e.target as HTMLElement).setPointerCapture?.(e.pointerId);
		pintando.current = true;
		setEnCurso({ t: herr, color, grosor, pts: [p, p] });
	};
	const mover = (e: React.PointerEvent) => {
		if (!pintando.current) return;
		e.preventDefault();
		const p = punto(e);
		setEnCurso((z) => (z ? { ...z, pts: z.t === "lapiz" ? [...z.pts, p] : [z.pts[0], p] } : z));
	};
	const soltar = () => {
		if (!pintando.current) return;
		pintando.current = false;
		setEnCurso((z) => {
			if (z) {
				const a = z.pts[0]; const b = z.pts[z.pts.length - 1];
				const chico = z.t !== "lapiz" && Math.abs(a.x - b.x) < 3 && Math.abs(a.y - b.y) < 3;
				if (!chico) setTrazos((t) => [...t, z]);
			}
			return null;
		});
	};

	// Deslizar para cambiar de foto (solo sin herramienta, como WhatsApp).
	const deslizBajar = (e: React.PointerEvent) => {
		if (herr || !varias || textoEn || guardando) return;
		if (e.pointerType === "mouse" && e.button !== 0) return;
		if ((e.target as HTMLElement).closest("button")) return;
		desliz.current = { x: e.clientX, y: e.clientY };
		(e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
	};
	const deslizMover = (e: React.PointerEvent) => {
		const d = desliz.current;
		if (!d) return;
		let v = e.clientX - d.x;
		if ((idx === 0 && v > 0) || (idx === fotos.length - 1 && v < 0)) v /= 3;   // en las orillas se resiste
		setDx(v);
	};
	const deslizSoltar = (e: React.PointerEvent) => {
		const d = desliz.current;
		if (!d) return;
		desliz.current = null;
		const ddx = e.clientX - d.x; const ddy = e.clientY - d.y;
		setDx(0);
		if (Math.abs(ddx) > 50 && Math.abs(ddx) > Math.abs(ddy)) ir(idx + (ddx < 0 ? 1 : -1));
	};

	/** Las fotos con marcas, ya convertidas en JPEG (null si algo falló). */
	const hornear = async (): Promise<FotoMarcada[] | null> => {
		const todas: Record<string, Trazo[]> = { ...marcas };
		if (textoEn && texto.trim()) todas[id] = [...(todas[id] || []), { t: "texto", color, grosor, pts: [textoEn], texto: texto.trim() }];
		const salida: FotoMarcada[] = [];
		for (const f of fotos) {
			const z = todas[f.id]; const b = bases.current.get(f.id);
			if (!z || !z.length || !b) continue;
			const c = document.createElement("canvas");
			c.width = b.width; c.height = b.height;
			const ctx = c.getContext("2d");
			if (!ctx) return null;
			ctx.drawImage(b, 0, 0);
			z.forEach((t) => pintar(ctx, t, unidadDe(b)));
			const blob = await aBlob(c, 0.9);
			if (!blob) return null;
			salida.push({ id: f.id, file: new File([blob], nombreJpg(f.file.name), { type: "image/jpeg", lastModified: Date.now() }) });
		}
		return salida;
	};
	const terminar = async (enviar: boolean) => {
		if (guardando) return;
		setGuardando(true);
		const hechas = await hornear();
		if (!montado.current) return;
		setGuardando(false);
		if (!hechas) return;
		if (enviar && onEnviar) onEnviar(hechas); else onListo(hechas);
	};

	// Dónde va la cajita de texto en pantalla (coordenadas del lienzo → CSS).
	const posTexto = (): React.CSSProperties => {
		const c = lienzo.current; const m = marco.current;
		if (!c || !m || !textoEn) return { display: "none" };
		const r = c.getBoundingClientRect(); const rm = m.getBoundingClientRect();
		const esc = r.width / c.width;
		return { left: r.left - rm.left + textoEn.x * esc, top: r.top - rm.top + textoEn.y * esc, fontSize: Math.max(14, unidad * 7 * grosor * esc), color };
	};

	const aviso = herr === "texto" ? "Toca donde quieras escribir; Enter para fijar el texto."
		: herr ? (herr === "lapiz" ? "Dibuja con el dedo o el mouse." : "Arrastra para dibujar.") + " Toca la herramienta otra vez para soltarla."
		: varias ? "Elige una herramienta para marcar. Desliza para cambiar de foto." : "Elige una herramienta para marcar.";

	return createPortal(
		<div className="mf-velo" role="dialog" aria-modal="true" aria-label="Marcar Foto">
			<div className="mf-cab">
				<button type="button" className="btn-wa" onClick={cancelar} disabled={guardando}>Cancelar</button>
				<span className="mf-titulo">{varias ? "Foto " + (idx + 1) + " de " + fotos.length : "Marcar Foto"}</span>
				<button type="button" className="btn-wa pri" onClick={() => terminar(false)} disabled={guardando}>{guardando ? "Guardando…" : "Listo"}</button>
			</div>
			<div className="mf-barra">
				<div className="mf-grupo mf-g-herr" role="group" aria-label="Herramienta">
					{HERRAMIENTAS.map((h) => (
						<button key={h.id} type="button" className={"mf-btn" + (herr === h.id ? " activo" : "")} title={h.nombre} aria-label={h.nombre} aria-pressed={herr === h.id}
							onClick={() => elegirHerr(h.id)}>{h.icono}</button>
					))}
				</div>
				<div className="mf-grupo mf-g-color" role="group" aria-label="Color">
					{COLORES.map((c) => (
						<button key={c} type="button" className={"mf-color" + (color === c ? " activo" : "")} style={{ background: c }} aria-label={"Color " + c} aria-pressed={color === c}
							onClick={() => { setColor(c); if (!herr) setHerr("lapiz"); }} />
					))}
				</div>
				<div className="mf-grupo mf-g-grosor" role="group" aria-label="Grosor">
					{GROSORES.map((g) => (
						<button key={g.id} type="button" className={"mf-btn" + (grosor === g.id ? " activo" : "")} title={g.nombre} aria-label={g.nombre} aria-pressed={grosor === g.id} onClick={() => setGrosor(g.id)}>
							<span className="mf-punto" style={{ width: 4 + g.id * 6, height: 4 + g.id * 6 }} />
						</button>
					))}
				</div>
				<div className="mf-grupo mf-g-deshacer">
					<button type="button" className="mf-btn" title="Deshacer (Ctrl+Z)" aria-label="Deshacer" disabled={!trazos.length} onClick={() => setTrazos((t) => t.slice(0, -1))}>
						<svg viewBox="0 0 24 24"><path d="M9 14L4 9l5-5M4 9h10a6 6 0 0 1 0 12h-3" /></svg>
					</button>
				</div>
			</div>
			<div className={"mf-marco" + (!herr && varias ? " desliza" : "")} ref={marco}
				onPointerDown={deslizBajar} onPointerMove={deslizMover} onPointerUp={deslizSoltar} onPointerCancel={deslizSoltar}>
				{noAbre ? (
					<div className="mf-aviso">Este navegador no puede abrir esta foto para marcarla. Se mandará tal cual.</div>
				) : !base ? (
					<div className="mf-aviso">Cargando foto…</div>
				) : null}
				<canvas ref={lienzo} className={"mf-lienzo " + (herr || "nada") + (dx ? " arrastrando" : "")}
					style={base ? { transform: dx ? "translateX(" + dx + "px)" : undefined } : { display: "none" }}
					onPointerDown={bajar} onPointerMove={mover} onPointerUp={soltar} onPointerCancel={soltar} onPointerLeave={soltar} />
				{textoEn ? (
					<textarea ref={cajaTexto} className="mf-texto" style={posTexto()} value={texto} rows={1} placeholder="Escribe…" aria-label="Texto" autoFocus
						onChange={(e) => setTexto(e.target.value)}
						onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); confirmarTexto(); } e.stopPropagation(); }}
						onBlur={() => { if (Date.now() - abiertoEn.current < 400) cajaTexto.current?.focus(); else confirmarTexto(); }} />
				) : null}
				{varias && idx > 0 ? <button type="button" className="mf-flecha izq" aria-label="Foto anterior" onClick={() => ir(idx - 1)}><svg viewBox="0 0 24 24"><path d="M15 5l-7 7 7 7" /></svg></button> : null}
				{varias && idx < fotos.length - 1 ? <button type="button" className="mf-flecha der" aria-label="Foto siguiente" onClick={() => ir(idx + 1)}><svg viewBox="0 0 24 24"><path d="M9 5l7 7-7 7" /></svg></button> : null}
			</div>
			<div className="mf-pie">{aviso}</div>
			{varias ? (
				<div className="mf-tira" ref={tira} role="tablist" aria-label="Fotos">
					{fotos.map((f, n) => (
						<button key={f.id} type="button" role="tab" aria-selected={n === idx} aria-label={"Foto " + (n + 1)} className={"mf-mini" + (n === idx ? " activa" : "")} onClick={() => ir(n)}>
							<img src={f.url} alt="" draggable={false} />
							{(marcas[f.id] || []).length ? <span className="mf-con-marcas" title="Tiene marcas"><WappIcono n="edit" /></span> : null}
						</button>
					))}
				</div>
			) : null}
			{onEnviar ? (
				<div className="mf-envio">
					{onComentario ? (
						<textarea rows={1} value={comentario || ""} placeholder="Comentario (opcional)" aria-label="Comentario" maxLength={1000} disabled={guardando}
							onChange={(e) => { onComentario(e.target.value); const t = e.target; t.style.height = "44px"; t.style.height = Math.min(110, t.scrollHeight) + "px"; }}
							onKeyDown={(e) => { e.stopPropagation(); if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) { e.preventDefault(); terminar(true); } }} />
					) : <span className="mf-esp" />}
					<button type="button" className="mf-enviar" onClick={() => terminar(true)} disabled={guardando} aria-label={textoEnviar || "Enviar"}>
						<svg viewBox="0 0 24 24"><path d="M4 12l16-8-6 16-2.5-6.5z" /></svg><span>{textoEnviar || "Enviar"}</span>
					</button>
				</div>
			) : null}
		</div>,
		document.body,
	);
};

export default MarcarFoto;
